import React, { useEffect, useMemo, useState } from 'react';
import './TOCModal.css';

const TOCModal = ({ isOpen, onClose, data }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const learningJourney = data?.learningJourney || [];
  const detailedTOC = data?.detailedTOC || [];

  const rows = useMemo(() => {
    const totalRows = Math.max(learningJourney.length, detailedTOC.length);
    return Array.from({ length: totalRows }, (_, index) => ({
      moduleName: learningJourney[index] || '',
      resource: detailedTOC[index] || '',
      serialNumber: index + 1,
    }));
  }, [learningJourney, detailedTOC]);

  const filteredRows = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) return rows;

    return rows.filter((row) => {
      const moduleText = (row.moduleName || '').toLowerCase();
      const resourceText = (row.resource || '').toLowerCase();
      return moduleText.includes(normalized) || resourceText.includes(normalized);
    });
  }, [rows, searchTerm]);

  const validLinks = useMemo(
    () => filteredRows.filter((row) => isHttpUrl(row.resource)).map((row) => row.resource),
    [filteredRows],
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEsc);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !data) return null;

  const handleCopyFirstLink = async () => {
    if (!validLinks.length || !navigator?.clipboard) return;
    try {
      await navigator.clipboard.writeText(validLinks[0]);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const openAllLinks = () => {
    validLinks.forEach((url) => {
      window.open(url, '_blank', 'noopener,noreferrer');
    });
  };

  return (
    <div className="toc-modal-overlay" onClick={onClose}>
      <div
        className="toc-modal-content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="toc-modal-title"
      >
        <div className="toc-modal-header">
          <div className="toc-modal-headerText">
            <h2 id="toc-modal-title" className="toc-modal-title">{data.schoolName}</h2>
            <p className="toc-modal-subtitle">
              Explore modules, search quickly, and open only the resources you need.
            </p>
            <div className="toc-modal-meta">
              <span className="toc-chip">{learningJourney.length} Modules</span>
              <span className="toc-chip">{validLinks.length} Links</span>
              {!!data.duration && <span className="toc-chip">{data.duration} Days</span>}
            </div>
          </div>
          <button className="toc-modal-close" onClick={onClose} aria-label="Close popup">
            x
          </button>
        </div>

        {/* <div className="toc-modal-toolbar">
          <input
            type="text"
            className="toc-search"
            placeholder="Search module or resource"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search module or resource"
          />
          <div className="toc-toolbar-actions">
            <button type="button" className="toc-actionBtn" onClick={handleCopyFirstLink} disabled={!validLinks.length}>
              Copy First Link
            </button>
            <button type="button" className="toc-actionBtn toc-actionBtn--primary" onClick={openAllLinks} disabled={!validLinks.length}>
              Open Visible Links
            </button>
          </div>
        </div> */}

        <div className="toc-modal-body">
          <div className="toc-tableWrap">
            <table className="toc-table">
              <thead>
                <tr>
                  <th>#</th>
                  {/* <th>School Name</th> */}
                  <th>Course/Module Name</th>
                  <th>Modulewise Workday Course URL</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={`${row.serialNumber}-${row.moduleName}-${row.resource}`}>
                    <td>{row.serialNumber}</td>
                    {/* <td>{data.schoolName}</td> */}
                    <td>{row.moduleName || '-'}</td>
                    <td>
                      {isHttpUrl(row.resource) ? (
                        <a href={row.resource} target="_blank" rel="noopener noreferrer">
                          {row.resource}
                        </a>
                      ) : (
                        <span className="toc-plainText">{row.resource || '-'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!filteredRows.length && (
            <div className="toc-emptyState">No items found for the current search.</div>
          )}

          <div className="toc-mobileCards">
            {filteredRows.map((row) => (
              <article className="toc-mobileCard" key={`mobile-${row.serialNumber}-${row.moduleName}`}>
                <div className="toc-mobileCard__badge">Module {row.serialNumber}</div>
                <h3 className="toc-mobileCard__title">{row.moduleName || 'Untitled Module'}</h3>
                <p className="toc-mobileCard__school">{data.schoolName}</p>
                {isHttpUrl(row.resource) ? (
                  <a href={row.resource} target="_blank" rel="noopener noreferrer" className="toc-mobileCard__link">
                    Open Resource
                  </a>
                ) : (
                  <p className="toc-mobileCard__text">{row.resource || 'Resource not available'}</p>
                )}
              </article>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

function isHttpUrl(value) {
  if (!value || typeof value !== 'string') return false;

  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export default TOCModal;
